import { useCallback, useEffect, useState } from 'react';
import { zoneService } from '../services/zoneService';
import type { ZoneStatisticsSummary } from '../types/zone';

/** Refresh cadence of the cross-zone pages (dashboard, Netzmonitoring). */
export const ZONE_STATISTICS_POLL_MS = 60_000;

export interface ZoneStatisticsState {
  statistics: ZoneStatisticsSummary[];
  isLoading: boolean;
  error: string | null;
  /** Browser time (ms) of the last successful load, null before the first one. */
  loadedAt: number | null;
  reload: () => Promise<void>;
}

/**
 * The lightweight summary of ALL zones (`GET /zones/statistics`) — the one sanctioned
 * cross-zone payload (frontend/CLAUDE.md → Zone Data Loading). Loads on mount and refreshes
 * every 60 s while the page is mounted; `zoneService` dedupes calls within 5 s, so two
 * consumers share one request.
 */
export function useZoneStatistics(): ZoneStatisticsState {
  const [statistics, setStatistics] = useState<ZoneStatisticsSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const stats = await zoneService.getZoneStatistics();
      setStatistics(stats);
      setLoadedAt(Date.now());
    } catch (err) {
      console.error('[useZoneStatistics] Failed to load statistics:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const interval = setInterval(() => void reload(), ZONE_STATISTICS_POLL_MS);
    return () => clearInterval(interval);
  }, [reload]);

  return { statistics, isLoading, error, loadedAt, reload };
}
